import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Heart, MapPin, Star, Wrench, Trash2 } from 'lucide-react';
import { getPostDetailPath } from '../../services/api/applications.service';

import API from '../../constants/api'
// API base via getApiBase()

export function MyFavoritesPanel() {
  const { token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, [token]);

  const fetchFavorites = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data?.data ?? response.data ?? [];
      setFavorites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Impossible de charger vos favoris');
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (postId) => {
    try {
      await axios.delete(`${API}/favorites/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter((f) => (f.postId ?? f.id) !== postId));
      toast.success('Retiré des favoris');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="documents-tab-favoris">
      <p className="text-slate-600 mb-6">Artisans et annonces que vous avez enregistrés.</p>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="lux-dashboard-card p-6 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-slate-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-5 bg-slate-200 rounded mb-2 w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : favorites.length === 0 ? (
        <div className="lux-dashboard-card p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun favori</h3>
          <p className="text-slate-600 mb-4">Parcourez l&apos;annuaire et ajoutez des artisans en favoris</p>
          <a href="https://clicketpro.fr/artisans">
            <Button>Voir les artisans</Button>
          </a>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {favorites.map((item) => {
            const post = item.post ?? item;
            const postId = post.id ?? item.postId;
            const title = post.title ?? item.nom_commercial ?? 'Annonce';
            const location = post.locationCity ?? item.localisation ?? '—';
            const detailPath = getPostDetailPath(post);

            return (
              <div key={item.id ?? postId} className="lux-dashboard-card p-6" data-testid={`favorite-${postId}`}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-7 h-7 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{title}</h3>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFavorite(postId)}
                        data-testid={`remove-${postId}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    {post.author?.name && (
                      <p className="text-sm text-slate-600 mb-2">{post.author.name}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {location}
                      </span>
                      {item.rating_average > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          {item.rating_average}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {detailPath ? (
                  <Link to={detailPath} className="block mt-4">
                    <Button variant="outline" className="w-full">Voir l&apos;annonce</Button>
                  </Link>
                ) : post.author?.id ? (
                  <Link to={`/artisans/${post.author.id}`} className="block mt-4">
                    <Button variant="outline" className="w-full">Voir le profil</Button>
                  </Link>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
